import { load as loadGlobals } from '@/api/globals'

const svelteImportsRegexp = /('|")svelte(\/[^('|")]+)?('|")/g

export async function loadComponents (platform, refs = [], options = {}) {
  if (!options.noMissing && !options.missing) {
    throw new Error('The options passed to loadComponents must contain a ref for a "missing" component')
  }

  const isRoot = !options.isChild

  options.isChild = true

  const promises = []

  for (const ref of [...new Set(refs)]) {
    promises.push((async function () {
      let mod = platform.components[ref]

      if (!ref || mod) {
        if (mod) {
          platform.api.core.dispatch('component.load', { ref, mod })
        }

        return
      }

      const loader = IS_BROWSER ? loadClientComponent : loadServerComponent

      mod = await loader(platform, ref, options)

      if (!mod) {
        return
      }

      mod.meta = mod.meta || {}
      mod.meta.ref = ref

      if (Array.isArray(mod.listeners)) {
        for (const listener of mod.listeners) {
          if (typeof listener.event !== 'string' || typeof listener.handler !== 'function') {
            continue
          }

          platform.listeners[listener.event] = platform.listeners[listener.event] || {}
          platform.listeners[listener.event][ref] = listener.handler
        }
      }

      if (mod.Components && Array.isArray(mod.Components.load)) {
        const dependencies = []

        for (let component of mod.Components.load) {
          if (Array.isArray(component)) {
            component = component[0]
          }

          if (typeof component !== 'string') {
            if (typeof component.condition === 'function') {
              const shouldLoad = await component.condition(platform)

              if (!shouldLoad) {
                continue
              }
            }

            component = component.ref
          }

          dependencies.push(component)
        }

        if (dependencies.length) {
          await loadComponents(platform, dependencies, options)
        }
      }

      platform.components[ref] = mod

      await platform.api.core.dispatch('component.load', { ref, mod })
    })())
  }

  await Promise.allSettled(promises)

  if (isRoot) {
    for (const [, component] of Object.entries(platform.components)) {
      await populateComponents(platform, component.Components, options)
    }
  }
}

const components = {}

export async function loadServerComponent (platform, ref, options) {
  const fullRef = `cms/components/${ref}`

  if (components[fullRef]) {
    return components[fullRef]
  }

  const fs = await import('fs')
  const path = await import('path')
  const externalComponentPath = path.join(__dirname, `../components/${ref}.server`)
  const { importModule } = await import('@/server/import')

  try {
    const source = await fs.promises.readFile(`${externalComponentPath}.js`, 'utf8')
    const imports = (source.match(svelteImportsRegexp) || []).map(s => s.replace(/'|"/g, ''))

    await loadGlobals(imports)

    const mod = importModule(externalComponentPath)

    if (!mod) {
      return null
    }

    components[fullRef] = mod

    await initModule(platform, fullRef, mod)

    return mod
  } catch (err) {
    console.error(err)

    return missing(platform, options.missing)
  }
}

const fetching = {}

export async function loadClientComponent (platform, ref, options) {
  const fullRef = `cms/components/${ref}`
  const jsURL = `/components/${ref}.client.js`
  const cssURL = `/components/${ref}.client.css`

  const mod = window[fullRef] || fetching[fullRef]

  if (mod) {
    return mod
  }

  fetching[fullRef] = (async function () {
    const res = await platform.fetch(`${jsURL}?c=${platform.storeData.cacheBust}`)

    if (!res.ok) {
      return missing(platform, options.missing)
    }

    const componentJsId = `${fullRef}-js`

    if (!document.getElementById(componentJsId) && !fetching[componentJsId]) {
      fetching[componentJsId] = true

      let source = await res.text()

      source = source.replace('var __JOLOJO_GLOBAL__', `window['${fullRef}']`)

      const imports = (source.match(svelteImportsRegexp) || []).map(s => s.replace(/'|"/g, ''))

      await loadGlobals(imports)

      const script = document.createElement('script')

      script.setAttribute('id', componentJsId)
      script.setAttribute('nonce', platform.storeData.nonce)
      script.textContent = platform.api.core.unsafe.trustedScript(source)

      document.head.appendChild(script)

      const mod = window[fullRef]

      const hasComponentCss = !!res.headers.get('x-has-component-css')
      const componentCssId = `${fullRef}-component-css`
      const loadCSS = typeof mod.meta?.css === 'undefined' || !!mod.meta.css

      if (hasComponentCss && loadCSS && !fetching[componentCssId]) {
        fetching[componentCssId] = true

        platform.fetch(`${cssURL}?c=${platform.storeData.cacheBust}`).then(async res => {
          const source = await res.text()
          const style = document.createElement('style')

          style.setAttribute('id', componentCssId)
          style.setAttribute('nonce', platform.storeData.nonce)
          style.textContent = source

          document.head.appendChild(style)
        })
      }
    }

    const mod = window[fullRef]

    if (!mod) {
      return null
    }

    await initModule(platform, fullRef, mod)

    return mod
  })()

  return fetching[fullRef]
}

async function initModule (platform, ref, mod) {
  compareCompilerVersions(ref, mod)

  // This function is run on both the server and client
  if (typeof mod.init === 'function') {
    await mod.init(platform)
  }
}

function compareCompilerVersions (ref, mod) {
  const external = mod.svelteCompilerVersion

  if (!external) {
    console.warn(`Warning: The component ${ref} does not have a Svelte version`)

    return
  }

  const { major, minor, patch } = SVELTE_COMPILER_VERSION
  const coreVersion = `${major}.${minor}.${patch}`
  const externalVersion = `${external.major}.${external.minor}.${external.patch}`

  if (coreVersion !== externalVersion) {
    console.warn(`
      Warning: A potentially incompatible external component has been loaded:
      - Compiled with Svelte v${coreVersion}: cms core
      - Compiled with Svelte v${externalVersion}: ${ref}
    `.replace(/^\s+/gm, '').trim())
  }
}

async function missing (platform, ref) {
  if (!platform.components[ref]) {
    await loadComponents(platform, [ref], { noMissing: true })
  }

  return platform.components[ref]
}

async function populateComponents (platform, Components, options) {
  if (Components && Array.isArray(Components.load)) {
    for (let i = 0; i < Components.load.length; i++) {
      let c = Components.load[i]

      if (Array.isArray(c)) {
        c[0] = c[0].replace(/^!/, '')
        c[1] = c[1].replace(/^!/, '')
      } else if (typeof c !== 'string') {
        if (typeof c.condition === 'function') {
          const shouldLoad = await c.condition(platform)

          if (!shouldLoad) {
            Components.load[i] = null

            continue
          }
        }

        c = c.ref
      }

      Components.load[i] = c.replace(/^!/, '')
    }

    Components.load = Components.load.filter(c => !!c)

    const counts = {}

    for (let name of Components.load) {
      if (typeof name === 'string') {
        name = [name, name]
      }

      const alias = name[1]
      const basename = alias.split('/').pop()

      counts[basename] = counts[basename] || 0

      counts[basename]++
    }

    for (let name of Components.load) {
      if (typeof name === 'string') {
        name = [name, name]
      }

      const ref = name[0]
      let alias = name[1]

      if (ref === alias) {
        const basename = alias.split('/').pop()

        if (counts[basename] === 1) {
          alias = basename
        }
      }

      let mod = platform.components[ref]

      if (!mod) {
        mod = platform.components[options.missing]
      }

      if (!mod) {
        continue
      }

      Components[alias] = mod.default
    }
  }
}
